import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Building, ChevronLeft, ChevronRight, ImageIcon, AlertCircle } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PendingSyncBadge } from '@/components/publishing/PendingSyncBadge';
import { BlockedStatusBadge } from '@/components/synced-locations/BlockedStatusBadge';
import { getProxiedImageUrl } from '@/lib/image-proxy';
import type { SyncedLocation } from '@/types/api';

// Strip markdown formatting for clean display
function stripMarkdown(text: string): string {
  return text.replace(/[*_~`]/g, '');
}

function getCoverImage(location: SyncedLocation): string | null {
  const images = location.partnerImages ?? [];
  if (images.length === 0) {
    return null;
  }
  // Get the image with the lowest order (or first one)
  const sorted = [...images].sort((a, b) => a.order - b.order);
  const first = sorted[0];
  return first?.url ?? null;
}

/**
 * Jump straight to a page number, so a list of 150+ pages does not need
 * Next clicked once per page. An invalid or unchanged number puts the box
 * back to the current page instead of requesting anything.
 */
function PageJump({
  page,
  totalPages,
  onJump,
}: {
  page: number;
  totalPages: number;
  onJump: (page: number) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(String(page));

  useEffect(() => {
    setDraft(String(page));
  }, [page]);

  const submit = () => {
    const target = Number(draft);
    if (Number.isInteger(target) && target >= 1 && target <= totalPages && target !== page) {
      onJump(target);
    } else {
      setDraft(String(page));
    }
  };

  return (
    <form
      className="flex items-center gap-2"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <label htmlFor="synced-locations-page-jump" className="text-sm">
        Go to page
      </label>
      <Input
        id="synced-locations-page-jump"
        type="number"
        inputMode="numeric"
        aria-label={`Go to page, 1 to ${String(totalPages)}`}
        min={1}
        max={totalPages}
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        className="h-8 w-20"
        data-testid="synced-locations-page-jump-input"
      />
      <Button type="submit" variant="outline" size="sm" data-testid="synced-locations-page-jump-submit">
        Go
      </Button>
    </form>
  );
}

interface SyncedLocationsListProps {
  locations: SyncedLocation[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  onPageChange: (page: number) => void;
  /** Whether selection mode is active */
  isSelectionMode?: boolean;
  /** Set of selected location IDs */
  selectedIds?: Set<string>;
  /** Handler to toggle selection of a location */
  onToggleSelection?: (id: string) => void;
  /** Handler to select/deselect all visible locations */
  onSelectAll?: (ids: string[]) => void;
  /** Handler to deselect all */
  onDeselectAll?: () => void;
}

function getEnrichmentBadge(status: string) {
  switch (status) {
    case 'pending':
      return <Badge variant="outline">Pending</Badge>;
    case 'enriched':
      return <Badge variant="success">Enriched</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

function getPublicationBadge(status: string) {
  switch (status) {
    case 'not_published':
      return <Badge variant="secondary">Not Published</Badge>;
    case 'pending_review':
      return <Badge variant="warning">Pending Review</Badge>;
    case 'published':
      return <Badge variant="success">Published</Badge>;
    case 'rejected':
      return <Badge variant="destructive">Rejected</Badge>;
    case 'unpublished':
      return <Badge variant="outline">Unpublished</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
}

/**
 * Shows "Not Ready" badge for enriched locations that cannot be published.
 * Displays validation errors in a tooltip on hover.
 */
function NotReadyBadge({ errors }: { errors: string[] }): JSX.Element {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="warning"
            className="cursor-help gap-1"
            data-testid="not-ready-badge"
          >
            <AlertCircle className="h-3 w-3" />
            Not Ready
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="font-medium mb-1">Missing required fields:</p>
          <ul className="list-disc pl-4 text-xs">
            {errors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * What a row is, in one line: its source, its kind, its first categories, and
 * whether the printed address disagrees with the pin. The filters run on the
 * same fields, so a person can see why a row matched.
 */
function LocationFacts({ location }: { location: SyncedLocation }): JSX.Element | null {
  const categories = (location.categories ?? []).filter((c) => c.includes('>')).slice(0, 2);
  const kinds = location.hostContactKinds ?? [];
  const mismatch = location.pinAddress?.matchesPrinted === false;
  if (!location.source && !location.assetClass && categories.length === 0 && kinds.length === 0 && !mismatch) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="location-facts">
      {location.source && <Badge variant="outline" className="text-[10px] font-normal">{location.source}</Badge>}
      {location.assetClass === 'poi' && <Badge variant="outline" className="text-[10px] font-normal">POI</Badge>}
      {categories.map((c) => (
        <Badge key={c} variant="secondary" className="text-[10px] font-normal">{c}</Badge>
      ))}
      {kinds.length > 0 && (
        <span className="text-[11px] text-muted-foreground" title={kinds.join(', ')}>
          contact: {kinds.join(', ')}
        </span>
      )}
      {mismatch && (
        <Badge variant="warning" className="text-[10px] font-normal" data-testid="address-mismatch-badge">
          address ≠ pin
        </Badge>
      )}
    </div>
  );
}

export function SyncedLocationsList({
  locations,
  pagination,
  isLoading,
  onPageChange,
  isSelectionMode = false,
  selectedIds = new Set(),
  onToggleSelection,
  onSelectAll,
  onDeselectAll,
}: SyncedLocationsListProps): JSX.Element {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-32 animate-pulse rounded-lg border bg-muted"
            data-testid="synced-location-skeleton"
          />
        ))}
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
        <Building className="mb-4 h-12 w-12 text-muted-foreground" />
        <p className="text-lg font-medium">No synced locations found</p>
        <p className="text-sm text-muted-foreground">
          Start syncing a partner account to see locations here.
        </p>
      </div>
    );
  }

  // Calculate select all state
  const allVisibleIds = locations.map((loc) => loc.id);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedIds.has(id));
  const someSelected = allVisibleIds.some((id) => selectedIds.has(id)) && !allSelected;

  // Radix checkbox uses 'indeterminate' as a checked state value
  const selectAllCheckedState = allSelected ? true : someSelected ? 'indeterminate' : false;

  const handleSelectAllChange = (checked: boolean | 'indeterminate'): void => {
    if (checked === true) {
      onSelectAll?.(allVisibleIds);
    } else {
      onDeselectAll?.();
    }
  };

  return (
    <div className="space-y-4">
      {/* Select All header - only show in selection mode */}
      {isSelectionMode && (
        <div
          className="flex items-center gap-3 px-2 py-2 border-b"
          data-testid="select-all-header"
        >
          <Checkbox
            id="select-all"
            checked={selectAllCheckedState}
            onCheckedChange={handleSelectAllChange}
            aria-label="Select all locations"
            data-testid="select-all-checkbox"
          />
          <label
            htmlFor="select-all"
            className="text-sm text-muted-foreground cursor-pointer"
          >
            Select All ({String(selectedIds.size)}/{String(allVisibleIds.length)})
          </label>
        </div>
      )}

      <div className="space-y-3" data-testid="synced-locations-list">
        {locations.map((location) => {
          const coverImage = getCoverImage(location);
          const isSelected = selectedIds.has(location.id);

          const cardContent = (
            <Card
              className={`transition-colors hover:bg-muted/50 ${isSelected ? 'ring-2 ring-primary' : ''}`}
              data-testid={`synced-location-${location.id}`}
            >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    {/* Checkbox - only in selection mode */}
                    {isSelectionMode && (
                      <div
                        className="flex items-center"
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => { onToggleSelection?.(location.id); }}
                          aria-label={`Select ${location.name}`}
                          data-testid={`select-location-${location.id}`}
                        />
                      </div>
                    )}

                    {/* Cover Image - uses 'thumb' preset (150px) for list thumbnails (FR-127) */}
                    <div className="h-24 w-32 flex-shrink-0 overflow-hidden rounded-md bg-muted">
                      {coverImage ? (
                        <img
                          src={getProxiedImageUrl(coverImage, 'thumb')}
                          alt={stripMarkdown(location.name)}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <ImageIcon className="h-8 w-8 text-muted-foreground/50" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex flex-1 flex-col justify-between">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <h3 className="font-medium">{location.name}</h3>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            {location.address?.city && location.address.country
                              ? `${location.address.city}, ${location.address.country}`
                              : location.address?.full ?? 'No address'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Partner: {location.partnerAccount?.name ?? 'Unknown'} ({location.partnerAccount?.partnerType?.name ?? 'Unknown'})
                          </div>
                          <LocationFacts location={location} />
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          {/* Show "Blocked" badge for blocked locations (highest priority) */}
                          {location.isBlocked && (
                            <BlockedStatusBadge reason={location.blockedReason} />
                          )}
                          {getEnrichmentBadge(location.enrichmentStatus)}
                          {getPublicationBadge(location.publicationStatus)}
                          {location.hasPendingSync && <PendingSyncBadge />}
                          {location.isGeneratingType && (
                            <Badge variant="secondary" data-testid="generating-type-badge">Generating type…</Badge>
                          )}
                          {/* Show "Not Ready" badge for enriched locations that can't be published */}
                          {!location.isBlocked &&
                           location.enrichmentStatus === 'enriched' &&
                           location.publicationStatus !== 'published' &&
                           location.publishability &&
                           !location.publishability.isPublishable &&
                           location.publishability.errors.length > 0 && (
                            <NotReadyBadge errors={location.publishability.errors} />
                          )}
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
                        {location.bedrooms !== null && <span>{location.bedrooms} bed</span>}
                        {location.bathrooms !== null && <span>{location.bathrooms} bath</span>}
                        {location.accommodates !== null && <span>Capacity {location.accommodates}</span>}
                        {location.imageCount !== undefined && <span>{location.imageCount} photo{location.imageCount === 1 ? '' : 's'}</span>}
                        {location.rating !== undefined && location.rating !== null && (
                          <span>★ {location.rating.toFixed(1)}{location.reviewCount ? ` (${String(location.reviewCount)})` : ''}</span>
                        )}
                        {location.partnerPrice && (
                          <span>{location.partnerPrice.amount.toLocaleString()} {location.partnerPrice.currency}{location.partnerPrice.unit ? `/${location.partnerPrice.unit}` : ''}</span>
                        )}
                        {location.lastSyncedAt && (
                          <span>Synced: {new Date(location.lastSyncedAt).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );

          // In selection mode, wrap with clickable div; otherwise, wrap with Link
          if (isSelectionMode) {
            return (
              <div
                key={location.id}
                className="block cursor-pointer"
                onClick={() => { onToggleSelection?.(location.id); }}
              >
                {cardContent}
              </div>
            );
          }

          return (
            <Link key={location.id} to={`/synced-locations/${location.id}`} className="block">
              {cardContent}
            </Link>
          );
        })}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-muted-foreground">
            Showing {(pagination.page - 1) * pagination.limit + 1} -{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { onPageChange(pagination.page - 1); }}
              disabled={pagination.page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <span className="text-sm">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { onPageChange(pagination.page + 1); }}
              disabled={pagination.page >= pagination.totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
            <PageJump page={pagination.page} totalPages={pagination.totalPages} onJump={onPageChange} />
          </div>
        </div>
      )}
    </div>
  );
}
